import React, { useState, useEffect, useContext } from 'react';  // Add useContext
import { Container, Form, Button, Spinner, OverlayTrigger, Tooltip, Alert, InputGroup, FormControl } from 'react-bootstrap';
import useUser from '../hooks/useUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faCopy, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate } from 'react-router-dom';
import './AddBot.css';
import { PaymentStatusContext } from '../contexts/PaymentStatusContext';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';

const PAYWALL_ALERT_MESSAGE = 'This feature is available for paying customers only. Please subscribe in order to generate or refine strategies.';

function AddBotPage() {
  const { isAuthenticated, getApiToken } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { isPayingCustomer } = useContext(PaymentStatusContext);

  // Form States
  const [botName, setBotName] = useState('');
  const [botDescription, setBotDescription] = useState('');
  const [botMode, setBotMode] = useState(''); 
  const [copyButtonText, setCopyButtonText] = useState("Copy");

  // UI States
  const [alertMessage, setAlertMessage] = useState('');
  const [alertVariant, setAlertVariant] = useState('success');
  const [API_KEY, setAPI_KEY] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [editing, setEditing] = useState(true);

  // Validation / Error States
  const [showValidationError, setShowValidationError] = useState(false);
  const [showMaxBotsError, setShowMaxBotsError] = useState(false);

  // Warn if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.warn("User is not authenticated. Add Bot requires login.");
    }
  }, [isAuthenticated]);

  // **Updated useEffect to Reset State on Location Change**
  useEffect(() => {
    // Reset all relevant states to initial values
    setEditing(true);
    setAPI_KEY('');
    setBotName('');
    setBotDescription('');
    setBotMode('');
    setShowValidationError(false);
    setShowMaxBotsError(false);
    setAlertMessage('');
    setAlertVariant('success');
    setIsLoading(false);
  }, [location.key]);

  useEffect(() => {
    if (isPayingCustomer && alertMessage === PAYWALL_ALERT_MESSAGE) {
      setAlertMessage('');
    }
  }, [isPayingCustomer, alertMessage]);

  // NEW: Function to check paying status and redirect if necessary
  const requirePaymentAction = () => {
    if (!isPayingCustomer) {
      pushEvent({
        event: ANALYTICS_EVENTS.PREMIUM_FEATURE_PROMPT,
        feature: 'add_bot',
        source: location.pathname,
        referrer: document.referrer
      });
      setAlertVariant('danger');
      setAlertMessage(PAYWALL_ALERT_MESSAGE);
      return true;
    }
    return false;
  };

  // Submit Handler
  const handleSubmit = async (event) => {
    event.preventDefault();

    // NEW: Check for paying status before submitting
    if (requirePaymentAction()) {
      pushEvent({
        event: ANALYTICS_EVENTS.SUBSCRIPTION_CTA_CLICKED,  // Changed from SUBSCRIPTION_STARTED
        source: 'add_bot_page',
        feature: 'add_bot'
      });
      return;
    }

    // Must have botName & botMode
    if (!botName.trim() || !botMode) {
      setShowValidationError(true);
      return;
    } else {
      setShowValidationError(false);
    }

    if (!isAuthenticated) {
      setAlertMessage('You must be logged in to add a bot.');
      setAlertVariant('danger');
      return;
    }

    setIsLoading(true);
    try {
      const token = await getApiToken();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/bots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: botName,
          description: botDescription,
          isPublic: false,
          botMode,
          isRealMoney: (botMode === 'real'),
        }),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        data = { error: true, message: 'Unknown error occurred.' };
      }

      if (response.ok) {
        pushEvent({
          event: ANALYTICS_EVENTS.BOT_CREATED,
          botMode: botMode,
          isPublic: false,
          source: location.pathname
        });
        setAlertMessage('Congratulations! Your Bot is Live in Private Mode.');
        setAlertVariant('success');
        setAPI_KEY(data.apiKey);
        setEditing(false);
      } else {
        pushEvent({
          event: ANALYTICS_EVENTS.BOT_CREATION_FAILED,
          error: data?.errorCode || 'unknown',
          source: location.pathname
        });
        const msg = data?.message || "Could not add bot. Please try again.";
        if (data?.errorCode === "MAX_BOTS_REACHED") {
          setShowMaxBotsError(true);
          setAlertMessage(msg);
        } else {
          setAlertMessage(msg);
          setAlertVariant('danger');
        }
      }
    } catch (error) {
      console.error("Error creating bot:", error);
      setAlertMessage('Could not add bot. Please try again.');
      setAlertVariant('danger');
    } finally {
      setIsLoading(false);
    }
  };

  // Copy to Clipboard
  const copyToClipboard = () => {
    if (API_KEY) {
      navigator.clipboard.writeText(API_KEY)
        .then(() => {
          setAlertMessage('API Key copied to clipboard!');
          setAlertVariant('success');
          setCopyButtonText("Copied!");
          setTimeout(() => setCopyButtonText("Copy"), 2000);
        })
        .catch(() => {
          setAlertMessage('Failed to copy API Key. Please copy manually.');
          setAlertVariant('danger');
        });
    }
  };

  return (
    <div className="add-bot-page fade-in">
      <Container className="top-section">
        <h1 className="main-heading slide-in-up">Add Your Trading Bot to BotSpot</h1>

        {/* inline Alert in place of Toast */}
        {alertMessage && (
          <Alert variant={alertVariant} dismissible onClose={() => setAlertMessage('')}>
            {alertMessage}
          </Alert>
        )}

        {editing && (
          <>
            <p className="main-subheading fade-in-delayed">
              <strong>Build Your Own:</strong> Use our <a href="/ai-agent" className="inline-link">AI Agent</a> to design custom strategies.
            </p>
            <p className="main-subheading fade-in-delayed" style={{ animationDelay:'0.3s' }}>
              <strong>Leverage Proven Bots:</strong> Launch Lumiwealth’s best bots directly from this site.
            </p>
            <p className="main-subheading fade-in-delayed" style={{ animationDelay:'0.6s' }}>
              <strong>Upgrade & Profit More:</strong> Go Pro or All Access at <a href="https://lumiwealth.com" className="inline-link" target="_blank" rel="noreferrer">lumiwealth.com</a> for premium strategies & support.
            </p>

            <Form className="bot-form bounce-in" onSubmit={handleSubmit}>
              {/* 1) Bot Name */}
              <Form.Group controlId="botName" className="mb-4 text-start">
                <Form.Label>
                  Bot Name{' '}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>A unique name helps you identify it easily.</Tooltip>}
                  >
                    <span>
                      <FontAwesomeIcon icon={faQuestionCircle} className="tooltip-icon" />
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 'CryptoCrush'"
                  value={botName}
                  onChange={(e) => editing ? setBotName(e.target.value) : null}
                  disabled={!editing}
                />
              </Form.Group>

              {/* 2) Trading Mode */}
              <Form.Group controlId="botMode" className="mb-4 text-start">
                <Form.Label>Trading Mode</Form.Label>
                <Form.Select
                  value={botMode}
                  onChange={(e) => editing && setBotMode(e.target.value)}
                  disabled={!editing}
                  required
                >
                  <option value="">-- Select an option --</option>
                  <option value="paper">Paper Trading (Test Mode)</option>
                  <option value="real">Real Money</option>
                </Form.Select>
              </Form.Group>

              {/* 3) Bot Description (Optional) */}
              <Form.Group controlId="botDescription" className="mb-4 text-start">
                <Form.Label>
                  Bot Description (Optional){' '}
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Describe your bot’s strategy (optional).</Tooltip>}
                  >
                    <span>
                      <FontAwesomeIcon icon={faQuestionCircle} className="tooltip-icon" />
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="What’s your bot’s approach? (Optional)"
                  value={botDescription}
                  onChange={(e) => editing ? setBotDescription(e.target.value) : null}
                  disabled={!editing}
                />
              </Form.Group>

              {/* Validation Alert (e.g., missing Bot Name or Trading Mode) */}
              {showValidationError && (
                <Alert variant="danger" onClose={() => setShowValidationError(false)} dismissible>
                  <Alert.Heading>Incomplete Information</Alert.Heading>
                  <p>Please provide a Bot Name and select a Trading Mode.</p>
                </Alert>
              )}

              {/* 5) Add Bot Button */}
              {!API_KEY && (
                <div className="text-center mt-4">
                  <OverlayTrigger 
                    placement="top" 
                    overlay={
                      !isPayingCustomer 
                      ? <Tooltip id="disabledTooltip">You are not a paying customer. Please subscribe in order to add a bot.</Tooltip>
                      : <></>
                    }
                  >
                    <span>
                      <Button
                        variant="primary"
                        type="submit"
                        className="addbot-submit-btn"
                        disabled={!editing || !isPayingCustomer}
                      >
                        {isLoading ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            {' '}Creating...
                          </>
                        ) : (
                          'Add Bot'
                        )}
                      </Button>
                    </span>
                  </OverlayTrigger>
                </div>
              )}
            </Form>
          </>
        )}

        {/* Success Panel */}
        {!editing && (
          <div className="success-panel slide-in-up">
            <Alert variant="info" className="success-alert">
              <strong>Success!</strong> {botName} is ready. Here’s your API key:
            </Alert>
            <div className="api-key-container">
              <div className="api-key-label">Your Bot’s API Key:</div>
              <InputGroup className="mb-3">
                <FormControl
                  readOnly
                  value={API_KEY}
                  className="api-key-text"
                />
                <Button
                  className="addbot-copy-btn"
                  size="sm"
                  onClick={copyToClipboard}
                >
                  <FontAwesomeIcon icon={faCopy} /> {copyButtonText}
                </Button>
              </InputGroup>
              <div className="api-key-instructions">
                Use this key with LumiBot or your trading platform to start running your strategy. Keep it secret and safe!
                
                <p>
                  You must set your <code>LUMIWEALTH_API_KEY</code> environment variable to this API key before running your bot. 
                  For local development, store it in a <strong>.env</strong> file; for Render, add it under “Environment” → “Environment Variables”; 
                  and for Repl.it, add it under “Secrets.”  
                  For more details, see our 
                  <a 
                    href="https://lumibot.lumiwealth.com/deployment.html" 
                    target="_blank" 
                    rel="noreferrer"
                  >
                    LumiBot Deployment Guide
                  </a>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Alerts */}
        <Container className="bottom-alerts">
          {showMaxBotsError && (
            <Alert variant="danger" onClose={() => setShowMaxBotsError(false)} dismissible>
              <Alert.Heading>Maximum Bots Reached</Alert.Heading>
              <p>
                {alertMessage || "Please delete an existing bot before adding a new one."}
              </p>
            </Alert>
          )}
        </Container>
      </Container>
    </div>
  );
}

export default AddBotPage;
